/**
 * emailValidation.ts - 統一メールアドレスバリデーションユーティリティ
 * FL-148: 全てのメールアドレス入力フォームに有効性バリデーションチェックを追加
 */

import logger from './logger';

/**
 * メールアドレスバリデーション結果の型定義
 */
export interface EmailValidationResult {
  /** バリデーション成功かどうか */
  isValid: boolean;
  /** エラーメッセージ（バリデーション失敗時） */
  error?: string;
  /** バリデーション警告（推奨事項など） */
  warning?: string;
}

/**
 * バリデーションオプションの型定義
 */
export interface EmailValidationOptions {
  /** 必須フィールドかどうか */
  required?: boolean;
  /** 空文字を許可するかどうか */
  allowEmpty?: boolean;
  /** より厳格なバリデーションを行うかどうか */
  strict?: boolean;
  /** 日本語ドメインを許可するかどうか */
  allowInternational?: boolean;
}

/**
 * デフォルトのバリデーションオプション
 */
const DEFAULT_OPTIONS: EmailValidationOptions = {
  required: false,
  allowEmpty: false,
  strict: true,
  allowInternational: true,
};

/**
 * メールアドレスの基本的な形式チェック（RFC 5322準拠）
 * 実用的なパターンを受け入れるよう調整
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * より簡易的なメールアドレス形式チェック（後方互換性用）
 */
const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 一般的でない文字を含むメールアドレスのチェック
 * 複数の@記号を確実に検出するため、@を除外しない
 */
const SUSPICIOUS_CHARS_REGEX = /[<>()[\]\\,;:\s"]/;

/**
 * よくある不正なドメインパターン
 */
const INVALID_DOMAIN_PATTERNS = [
  /\.{2,}/, // 連続するドット
  /^\./, // 先頭のドット
  /\.$/, // 末尾のドット
  /@\./, // @直後のドット
  /\.@/, // @直前のドット
];

/**
 * 推奨されないが有効なパターンのチェック
 */
const WARNING_PATTERNS = [
  { pattern: /.{60,}@/, message: 'ローカル部が長すぎる可能性があります' },
  {
    pattern: /\+/,
    message:
      'エイリアス（+記号）を含むメールアドレスは一部のシステムで問題が発生する場合があります',
  },
  { pattern: /^[^.]*@/, message: 'ローカル部にドットを含まないメールアドレスは推奨されません' },
];

/**
 * 統一されたメールアドレスバリデーション関数
 *
 * @param email - 検証するメールアドレス
 * @param options - バリデーションオプション
 * @returns バリデーション結果
 */
export function validateEmail(
  email: string | null | undefined,
  options: EmailValidationOptions = {}
): EmailValidationResult {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    // null/undefined チェック
    if (email === null || email === undefined) {
      if (opts.required) {
        return {
          isValid: false,
          error: 'メールアドレスは必須です',
        };
      }
      return { isValid: !opts.required };
    }

    // 文字列への変換
    const emailStr = String(email).trim();

    // 空文字チェック
    if (emailStr === '') {
      if (opts.required && !opts.allowEmpty) {
        return {
          isValid: false,
          error: 'メールアドレスを入力してください',
        };
      }
      return { isValid: opts.allowEmpty || !opts.required };
    }

    // 長さチェック（RFC 5321の制限）- 最初にチェック
    if (emailStr.length > 254) {
      return {
        isValid: false,
        error: 'メールアドレスが長すぎます（254文字以下）',
      };
    }

    // @ の個数チェック（優先的に実行）
    const atCount = (emailStr.match(/@/g) || []).length;
    if (atCount !== 1) {
      return {
        isValid: false,
        error:
          atCount === 0
            ? 'メールアドレスに@が含まれていません'
            : 'メールアドレスに@が複数含まれています',
      };
    }

    // 基本的な文字チェック（@以外の不正文字）
    if (SUSPICIOUS_CHARS_REGEX.test(emailStr)) {
      return {
        isValid: false,
        error: '無効な文字が含まれています',
      };
    }

    // ローカル部とドメイン部の分離
    const [localPart, domainPart] = emailStr.split('@');

    // ローカル部の検証
    if (localPart.length === 0) {
      return {
        isValid: false,
        error: 'ユーザー名部分が空です',
      };
    }

    if (localPart.length > 64) {
      return {
        isValid: false,
        error: 'ユーザー名部分が長すぎます（64文字以下）',
      };
    }

    // ローカル部の連続ドット検出
    if (localPart.includes('..')) {
      return {
        isValid: false,
        error: '無効なメールアドレス形式です',
      };
    }

    // ドメイン部の検証
    if (domainPart.length === 0) {
      return {
        isValid: false,
        error: 'ドメイン部分が空です',
      };
    }

    if (domainPart.length > 255) {
      return {
        isValid: false,
        error: 'ドメイン部分が長すぎます（255文字以下）',
      };
    }

    // 不正なドメインパターンのチェック
    for (const pattern of INVALID_DOMAIN_PATTERNS) {
      if (pattern.test(domainPart)) {
        return {
          isValid: false,
          error: '無効なドメイン形式です',
        };
      }
    }

    // TLD（トップレベルドメイン）の検証を先に実行
    if (!domainPart.includes('.')) {
      return {
        isValid: false,
        error: '有効なトップレベルドメインがありません',
      };
    }

    const tld = domainPart.split('.').pop();
    if (!tld || tld.length < 2) {
      return {
        isValid: false,
        error: '有効なトップレベルドメインがありません',
      };
    }

    // 正規表現による形式チェック（TLD検証後に実行）
    const regexToUse = opts.strict ? EMAIL_REGEX : SIMPLE_EMAIL_REGEX;
    if (!regexToUse.test(emailStr)) {
      return {
        isValid: false,
        error: '正しいメールアドレスの形式で入力してください',
      };
    }

    // 警告パターンのチェック
    let warning: string | undefined;
    for (const warningPattern of WARNING_PATTERNS) {
      if (warningPattern.pattern.test(emailStr)) {
        warning = warningPattern.message;
        break; // 最初の警告のみ表示
      }
    }

    logger.debug('Email validation successful:', { email: emailStr });

    return {
      isValid: true,
      warning,
    };
  } catch (error) {
    logger.error('Email validation error:', error);
    return {
      isValid: false,
      error: 'バリデーション処理でエラーが発生しました',
    };
  }
}

/**
 * シンプルなメールアドレス検証（後方互換性用）
 *
 * @param email - 検証するメールアドレス
 * @returns 有効かどうか
 */
export function isValidEmail(email: string | null | undefined): boolean {
  return validateEmail(email).isValid;
}

/**
 * 必須メールアドレス検証
 *
 * @param email - 検証するメールアドレス
 * @returns バリデーション結果
 */
export function validateRequiredEmail(email: string | null | undefined): EmailValidationResult {
  return validateEmail(email, { required: true });
}

/**
 * 厳格なメールアドレス検証（推奨）
 *
 * @param email - 検証するメールアドレス
 * @returns バリデーション結果
 */
export function validateEmailStrict(email: string | null | undefined): EmailValidationResult {
  return validateEmail(email, { strict: true, required: false });
}

/**
 * React Hook Form用のバリデーション関数
 *
 * @param required - 必須フィールドかどうか
 * @returns バリデーション関数
 */
export function createEmailValidator(required = false) {
  return (value: string | null | undefined) => {
    const result = validateEmail(value, { required });
    if (result.isValid) return true;

    // 特定のエラーを汎用メッセージに統一
    if (
      result.error?.includes('@が含まれていません') ||
      result.error?.includes('無効な文字') ||
      result.error?.includes('正しいメールアドレスの形式')
    ) {
      return '無効なメールアドレスです';
    }

    return result.error || '無効なメールアドレスです';
  };
}

/**
 * useFormValidation hook用のバリデーションルール
 */
export const emailValidationRule = {
  validator: (value: unknown) => {
    if (!value) return true; // 空の場合はOK（必須チェックは別途）
    return validateEmail(String(value)).isValid;
  },
  message: '正しいメールアドレスの形式で入力してください',
};

/**
 * 必須メールアドレス用のバリデーションルール
 */
export const requiredEmailValidationRule = {
  validator: (value: unknown) => {
    return validateEmail(String(value), { required: true }).isValid;
  },
  message: 'メールアドレスを入力してください',
};
